import React from 'react';
import { useTranslation } from 'react-i18next';
import { CountryAutocomplete } from './CountryAutocomplete';
import type { CreateQuoteFieldErrors, FormDataState } from '../../hooks/useCreateQuote';

interface RouteDetailsSectionProps {
  formData: FormDataState;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleCountryChange: (name: string, value: string) => void;
  fieldErrors: CreateQuoteFieldErrors['route'];
  inputClass: string;
  labelClass: string;
}

export const RouteDetailsSection: React.FC<RouteDetailsSectionProps> = ({
  formData,
  handleChange,
  handleCountryChange,
  fieldErrors,
  inputClass,
  labelClass
}) => {
  const { t } = useTranslation();
  const sectionTitleClass = "text-lg leading-none tracking-tight mb-4 text-foreground";
  const sectionId = 'route-details-section-title';
  const RequiredMark = () => <span className="text-red-500 ml-1">*</span>;
  const renderFieldErrors = (errors: string[]) => (
    errors.length > 0 ? (
      <ul className="mt-2 space-y-1 text-sm text-destructive">
        {errors.map((error, index) => (
          <li key={`${error}-${index}`}>{t(error)}</li>
        ))}
      </ul>
    ) : null
  );

  return (
    <div role="group" aria-labelledby={sectionId}>
      <h4 id={sectionId} className={sectionTitleClass}>{t('quote.form.sections.route')}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h5 className="font-medium text-muted-foreground">{t('quote.form.fields.origin')}</h5>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className={labelClass}>{t('quote.form.fields.country')}<RequiredMark /></label>
              <CountryAutocomplete
                name="origin_country"
                value={formData.origin_country}
                onChange={handleCountryChange}
                placeholder={t('quote.form.placeholders.select_country')}
                noResultsText={t('quote.form.placeholders.no_countries_found')}
                required
                className={inputClass}
              />
              {renderFieldErrors(fieldErrors.origin_country)}
            </div>
            <div className="col-span-1">
              <label className={labelClass}>{t('quote.form.fields.zip_code')}<RequiredMark /></label>
              <input
                type="text"
                name="origin_zip_code"
                className={inputClass}
                value={formData.origin_zip_code}
                onChange={handleChange}
                required
                placeholder={t('quote.form.placeholders.zip_origin')}
              />
              {renderFieldErrors(fieldErrors.origin_zip_code)}
            </div>
            <div className="col-span-1">
              <label className={labelClass}>{t('quote.form.fields.city')}<RequiredMark /></label>
              <input
                type="text"
                name="origin_city"
                className={inputClass}
                value={formData.origin_city}
                onChange={handleChange}
                required
                placeholder={t('quote.form.placeholders.city_origin')}
              />
              {renderFieldErrors(fieldErrors.origin_city)}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t('quote.form.fields.name')}</label>
            <input
              type="text"
              name="origin_name"
              className={inputClass}
              value={formData.origin_name}
              onChange={handleChange}
              placeholder={t('quote.form.placeholders.name_origin')}
            />
          </div>

          <div>
            <label className={labelClass}>{t('quote.form.fields.street')}</label>
            <input
              type="text"
              name="origin_street"
              className={inputClass}
              value={formData.origin_street}
              onChange={handleChange}
              placeholder={t('quote.form.placeholders.street_origin')}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h5 className="font-medium text-muted-foreground">{t('quote.form.fields.destination')}</h5>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className={labelClass}>{t('quote.form.fields.country')}<RequiredMark /></label>
              <CountryAutocomplete
                name="destination_country"
                value={formData.destination_country}
                onChange={handleCountryChange}
                placeholder={t('quote.form.placeholders.select_country')}
                noResultsText={t('quote.form.placeholders.no_countries_found')}
                required
                className={inputClass}
              />
              {renderFieldErrors(fieldErrors.destination_country)}
            </div>
            <div className="col-span-1">
              <label className={labelClass}>{t('quote.form.fields.zip_code')}<RequiredMark /></label>
              <input
                type="text"
                name="destination_zip_code"
                className={inputClass}
                value={formData.destination_zip_code}
                onChange={handleChange}
                required
                placeholder={t('quote.form.placeholders.zip_dest')}
              />
              {renderFieldErrors(fieldErrors.destination_zip_code)}
            </div>
            <div className="col-span-1">
              <label className={labelClass}>{t('quote.form.fields.city')}<RequiredMark /></label>
              <input
                type="text"
                name="destination_city"
                className={inputClass}
                value={formData.destination_city}
                onChange={handleChange}
                required
                placeholder={t('quote.form.placeholders.city_dest')}
              />
              {renderFieldErrors(fieldErrors.destination_city)}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t('quote.form.fields.name')}</label>
            <input
              type="text"
              name="destination_name"
              className={inputClass}
              value={formData.destination_name}
              onChange={handleChange}
              placeholder={t('quote.form.placeholders.name_dest')}
            />
          </div>

          <div>
            <label className={labelClass}>{t('quote.form.fields.street')}</label>
            <input
              type="text"
              name="destination_street"
              className={inputClass}
              value={formData.destination_street}
              onChange={handleChange}
              placeholder={t('quote.form.placeholders.street_dest')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
